import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, tokens } from '@/design';
import type { ScaleBarPropertiesPatch } from './layoutDocument';
import {
  effectiveScaleBarDenominator,
  scaleBarElementLayout,
} from './scaleBarGeometry';
import {
  ALL_SCALE_BAR_STYLES,
  scaleBarStyleMeta,
} from './scaleBarStyles';
import { ScaleBarTextSymbolPanel } from './ScaleBarTextSymbolPanel';
import type {
  MapFrameElement,
  ScaleBarDistanceUnit,
  ScaleBarElement,
  ScaleBarFittingStrategy,
  ScaleBarUnitSettings,
} from './layoutTypes';

export interface ScaleBarPropertiesPanelProps {
  readonly scaleBar: ScaleBarElement;
  readonly frames: readonly MapFrameElement[];
  readonly onChange: (patch: ScaleBarPropertiesPatch) => void;
  readonly onDelete: () => void;
  readonly onClose: () => void;
}

type PanelTab = 'scaleBar' | 'textSymbol';
type SectionId = 'general' | 'scaleBar' | 'mapUnits' | 'fitting' | 'divisions';

const UNITS: readonly {
  readonly value: ScaleBarDistanceUnit;
  readonly label: string;
}[] = [
  { value: 'meters', label: 'Meters' },
  { value: 'kilometers', label: 'Kilometers' },
  { value: 'feet', label: 'Feet' },
  { value: 'miles', label: 'Miles' },
];

const FITTING: readonly {
  readonly value: ScaleBarFittingStrategy;
  readonly label: string;
  readonly dual: boolean;
}[] = [
  { value: 'adjustDivision', label: 'Adjust division value', dual: true },
  { value: 'adjustDivisions', label: 'Adjust number of divisions', dual: true },
  {
    value: 'adjustDivisionAndDivisions',
    label: 'Adjust divisions and division value',
    dual: false,
  },
  { value: 'adjustWidth', label: 'Adjust width', dual: true },
  { value: 'fixedBarWidth', label: 'Fixed bar width', dual: false },
];

function Section({
  id,
  title,
  open,
  onToggle,
  children,
}: {
  readonly id: SectionId;
  readonly title: string;
  readonly open: boolean;
  readonly onToggle: (id: SectionId) => void;
  readonly children: ReactNode;
}) {
  return (
    <section>
      <button
        type="button"
        aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
        aria-expanded={open}
        onClick={() => onToggle(id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[1],
          width: '100%',
          minHeight: tokens.density.rowDense,
          padding: 0,
          border: 0,
          background: 'transparent',
          color: tokens.color.ink.primary,
          cursor: 'pointer',
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.md,
          fontWeight: tokens.font.weight.semibold,
          textAlign: 'left',
        }}
      >
        <Icon name={open ? 'expand_more' : 'chevron_right'} size={16} decorative />
        {title}
      </button>
      {open ? (
        <div
          style={{
            display: 'grid',
            gap: tokens.space[2],
            padding: `0 0 ${tokens.space[3]}px ${tokens.space[5]}px`,
          }}
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}

function boundedNumber(
  raw: string,
  min: number,
  max: number,
  fallback: number,
): number {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
}

export function ScaleBarPropertiesPanel({
  scaleBar,
  frames,
  onChange,
  onDelete,
  onClose,
}: ScaleBarPropertiesPanelProps) {
  const [tab, setTab] = useState<PanelTab>('scaleBar');
  const [openSections, setOpenSections] = useState<ReadonlySet<SectionId>>(
    () => new Set<SectionId>(['scaleBar', 'mapUnits', 'fitting', 'divisions']),
  );
  const frame =
    frames.find((candidate) => candidate.id === scaleBar.mapFrameId)
    ?? frames[0]
    ?? null;
  const denominator = frame
    ? effectiveScaleBarDenominator(scaleBar, frame.camera)
    : null;
  const geometry = useMemo(
    () => scaleBarElementLayout(scaleBar, denominator),
    [denominator, scaleBar],
  );
  const dual = scaleBar.units === 'dual' && scaleBar.lower != null;

  const toggleSection = (id: SectionId) => {
    setOpenSections((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const field: CSSProperties = {
    minHeight: tokens.density.rowDense,
    width: '100%',
    boxSizing: 'border-box',
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
  const row: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${tokens.density.touchMin * 2.8}px minmax(0, 1fr)`,
    alignItems: 'center',
    gap: tokens.space[2],
    fontSize: tokens.font.size.sm,
  };
  const iconButton: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: tokens.density.rowDense,
    minHeight: tokens.density.rowDense,
    border: 0,
    background: 'transparent',
    color: tokens.color.ink.secondary,
    cursor: 'pointer',
  };
  const tabButton = (active: boolean): CSSProperties => ({
    minHeight: tokens.density.rowDefault,
    padding: `0 ${tokens.space[1]}px`,
    border: 0,
    borderBottom: `${tokens.focus.ringWidth}px solid ${
      active ? tokens.color.semantic.info : 'transparent'
    }`,
    background: 'transparent',
    color: active ? tokens.color.semantic.info : tokens.color.ink.secondary,
    cursor: 'pointer',
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.md,
  });

  const unitEditor = (
    label: string,
    settings: ScaleBarUnitSettings,
    key: 'upper' | 'lower',
  ) => (
    <label style={row}>
      <span>{label}</span>
      <select
        aria-label={label}
        value={settings.unit}
        onChange={(event) => {
          const unit = event.target.value as ScaleBarDistanceUnit;
          onChange({
            [key]: {
              unit,
              divisionValue: null,
            },
            ...(key === 'upper' && !dual
              ? {
                  units:
                    unit === 'feet' || unit === 'miles'
                      ? 'imperial' as const
                      : 'metric' as const,
                }
              : {}),
          });
        }}
        style={field}
      >
        {UNITS.map((unit) => (
          <option key={unit.value} value={unit.value}>{unit.label}</option>
        ))}
      </select>
    </label>
  );

  const divisionEditor = (
    title: string,
    settings: ScaleBarUnitSettings,
    key: 'upper' | 'lower',
    effectiveValue: number | undefined,
  ) => {
    const divisionValueDisabled =
      scaleBar.fittingStrategy === 'adjustDivision'
      || scaleBar.fittingStrategy === 'adjustDivisionAndDivisions'
      || scaleBar.fittingStrategy === 'fixedBarWidth';
    const divisionsDisabled =
      scaleBar.fittingStrategy === 'adjustDivisions'
      || scaleBar.fittingStrategy === 'adjustDivisionAndDivisions';
    return (
      <fieldset
        style={{
          display: 'grid',
          gap: tokens.space[2],
          margin: 0,
          padding: tokens.space[2],
          border: tokens.border.thin,
          borderRadius: tokens.radius.input,
        }}
      >
        <legend
          style={{
            padding: `0 ${tokens.space[1]}px`,
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.xs,
          }}
        >
          {title}
        </legend>
        <label style={row}>
          <span>Division value</span>
          <input
            aria-label={`${title} division value`}
            type="number"
            min={0.000001}
            step="any"
            disabled={divisionValueDisabled}
            value={settings.divisionValue ?? effectiveValue ?? ''}
            onChange={(event) =>
              onChange({
                [key]: {
                  divisionValue: boundedNumber(
                    event.target.value,
                    0.000001,
                    1_000_000_000,
                    effectiveValue ?? 1,
                  ),
                },
              })
            }
            style={field}
          />
        </label>
        <label style={row}>
          <span>Divisions</span>
          <input
            aria-label={`${title} divisions`}
            type="number"
            min={1}
            max={50}
            disabled={divisionsDisabled}
            value={settings.divisions}
            onChange={(event) =>
              onChange({
                [key]: {
                  divisions: Math.round(
                    boundedNumber(event.target.value, 1, 50, settings.divisions),
                  ),
                },
              })
            }
            style={field}
          />
        </label>
        <label style={row}>
          <span>Subdivisions</span>
          <input
            aria-label={`${title} subdivisions`}
            type="number"
            min={1}
            max={20}
            value={settings.subdivisions}
            onChange={(event) =>
              onChange({
                [key]: {
                  subdivisions: Math.round(
                    boundedNumber(
                      event.target.value,
                      1,
                      20,
                      settings.subdivisions,
                    ),
                  ),
                },
              })
            }
            style={field}
          />
        </label>
      </fieldset>
    );
  };

  return (
    <aside
      data-testid="scale-bar-properties-panel"
      aria-label="Scale bar properties"
      style={{
        position: 'absolute',
        zIndex: tokens.zIndex.dock,
        top: tokens.space[3],
        right: tokens.space[3],
        width: tokens.layout.popoverLg,
        maxHeight: `calc(100% - ${tokens.space[6]}px)`,
        overflowY: 'auto',
        padding: tokens.space[3],
        border: `${tokens.focus.ringWidth}px solid ${tokens.color.semantic.info}`,
        borderRadius: tokens.radius.lg,
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        boxShadow: tokens.elevation[2],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: tokens.space[2],
        }}
      >
        <div>
          <div style={{ color: tokens.color.semantic.info, fontSize: tokens.font.size.lg }}>
            Element
          </div>
          <strong style={{ fontSize: tokens.font.size.md }}>
            {dual ? 'Dual Scale Bar' : 'Scale Bar'}
          </strong>
        </div>
        <div style={{ display: 'flex', gap: tokens.space[1] }}>
          <button
            type="button"
            aria-label="Delete scale bar"
            disabled={scaleBar.locked}
            title={scaleBar.locked ? 'Unlock the scale bar first' : undefined}
            onClick={onDelete}
            style={{
              ...iconButton,
              color: scaleBar.locked
                ? tokens.color.ink.muted
                : tokens.color.semantic.danger,
            }}
          >
            <Icon name="delete" size={20} decorative />
          </button>
          <button
            type="button"
            aria-label="Close scale bar properties"
            onClick={onClose}
            style={iconButton}
          >
            <Icon name="close" size={20} decorative />
          </button>
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Scale bar property tabs"
        style={{ display: 'flex', gap: tokens.space[4], marginTop: tokens.space[2] }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'scaleBar'}
          onClick={() => setTab('scaleBar')}
          style={tabButton(tab === 'scaleBar')}
        >
          {dual ? 'Dual Scale Bar' : 'Scale Bar'}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'textSymbol'}
          onClick={() => setTab('textSymbol')}
          style={tabButton(tab === 'textSymbol')}
        >
          Text Symbol
        </button>
      </div>

      <div
        aria-hidden
        style={{
          display: 'flex',
          gap: tokens.space[2],
          padding: `${tokens.space[3]}px 0`,
          borderBottom: tokens.border.thin,
          color: tokens.color.semantic.info,
        }}
      >
        <Icon name="description" size={24} decorative />
        <Icon name="linear_scale" size={24} decorative />
        <Icon name="crop" size={24} decorative />
        <Icon name="image" size={24} decorative />
      </div>

      {tab === 'textSymbol' ? (
        <ScaleBarTextSymbolPanel
          value={scaleBar.textSymbol}
          onApply={(textSymbol) => onChange({ textSymbol })}
          onBack={() => setTab('scaleBar')}
        />
      ) : (
        <div role="tabpanel" aria-label="Scale bar options">
          <h3
            style={{
              margin: `${tokens.space[3]}px 0 ${tokens.space[2]}px`,
              fontSize: tokens.font.size.lg,
              fontWeight: tokens.font.weight.regular,
            }}
          >
            Options
          </h3>

          <Section
            id="general"
            title="General"
            open={openSections.has('general')}
            onToggle={toggleSection}
          >
            <label style={row}>
              <span>Name</span>
              <input
                aria-label="Scale bar name"
                value={scaleBar.name}
                onChange={(event) => onChange({ name: event.target.value })}
                style={field}
              />
            </label>
            <label style={{ display: 'flex', gap: tokens.space[2] }}>
              <input
                type="checkbox"
                aria-label="Scale bar visible"
                checked={scaleBar.visible}
                onChange={(event) => onChange({ visible: event.target.checked })}
              />
              Visible
            </label>
            <label style={{ display: 'flex', gap: tokens.space[2] }}>
              <input
                type="checkbox"
                aria-label="Scale bar locked"
                checked={scaleBar.locked}
                onChange={(event) => onChange({ locked: event.target.checked })}
              />
              Locked
            </label>
          </Section>

          <Section
            id="scaleBar"
            title="Scale Bar"
            open={openSections.has('scaleBar')}
            onToggle={toggleSection}
          >
            <label style={row}>
              <span>Map Frame</span>
              <select
                aria-label="Scale bar map frame"
                value={scaleBar.mapFrameId}
                onChange={(event) => onChange({ mapFrameId: event.target.value })}
                style={field}
              >
                {frames.map((candidate, index) => (
                  <option key={candidate.id} value={candidate.id}>
                    {frames.length === 1 ? 'Map Frame' : `Map Frame ${index + 1}`}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', gap: tokens.space[2] }}>
              <input
                type="checkbox"
                aria-label="Compute scale at center"
                checked={scaleBar.computeAtCenter}
                onChange={(event) =>
                  onChange({ computeAtCenter: event.target.checked })
                }
              />
              Compute at center
            </label>
            <label style={row}>
              <span>Symbol</span>
              <select
                aria-label="Scale bar symbol"
                value={scaleBar.styleId}
                onChange={(event) => {
                  const styleId = event.target.value as ScaleBarElement['styleId'];
                  const meta = scaleBarStyleMeta(styleId);
                  if (meta.dual && meta.defaultUnits) {
                    onChange({
                      styleId,
                      units: 'dual',
                      upper: { unit: meta.defaultUnits[0], divisionValue: null },
                      lower: {
                        unit: meta.defaultUnits[1],
                        divisionValue: null,
                        divisions: scaleBar.upper.divisions,
                        subdivisions: scaleBar.upper.subdivisions,
                      },
                    });
                  } else {
                    onChange({
                      styleId,
                      units: scaleBar.units === 'dual' ? 'metric' : scaleBar.units,
                      ...(scaleBar.units === 'dual'
                        ? {
                            upper: {
                              unit: 'kilometers' as const,
                              divisionValue: null,
                            },
                          }
                        : {}),
                      lower: null,
                    });
                  }
                }}
                style={field}
              >
                {ALL_SCALE_BAR_STYLES.map((style) => (
                  <option key={style.id} value={style.id}>{style.label}</option>
                ))}
              </select>
            </label>
          </Section>

          <Section
            id="mapUnits"
            title="Map Units"
            open={openSections.has('mapUnits')}
            onToggle={toggleSection}
          >
            {unitEditor(dual ? 'Upper Unit' : 'Unit', scaleBar.upper, 'upper')}
            {dual && scaleBar.lower
              ? unitEditor('Lower Unit', scaleBar.lower, 'lower')
              : null}
            <label style={row}>
              <span>Horizontal Offset</span>
              <input
                aria-label="Scale bar horizontal offset"
                type="number"
                min={-72}
                max={72}
                step={0.5}
                value={scaleBar.horizontalOffsetPt}
                onChange={(event) =>
                  onChange({
                    horizontalOffsetPt: boundedNumber(
                      event.target.value,
                      -72,
                      72,
                      scaleBar.horizontalOffsetPt,
                    ),
                  })
                }
                style={field}
              />
            </label>
            <label style={row}>
              <span>Vertical Offset</span>
              <input
                aria-label="Scale bar vertical offset"
                type="number"
                min={-72}
                max={72}
                step={0.5}
                value={scaleBar.verticalOffsetPt}
                onChange={(event) =>
                  onChange({
                    verticalOffsetPt: boundedNumber(
                      event.target.value,
                      -72,
                      72,
                      scaleBar.verticalOffsetPt,
                    ),
                  })
                }
                style={field}
              />
            </label>
            <div style={row}>
              <span>Symbol</span>
              <button
                type="button"
                aria-label="Edit scale bar unit text symbol"
                title="Format the scale-bar unit and number text"
                onClick={() => setTab('textSymbol')}
                style={{
                  minHeight: tokens.density.rowDense,
                  border: 0,
                  borderRadius: tokens.radius.input,
                  background: 'transparent',
                  color: tokens.color.ink.primary,
                  cursor: 'pointer',
                  fontFamily: scaleBar.textSymbol.fontFamily,
                  fontSize: tokens.font.size.lg,
                  fontWeight:
                    scaleBar.textSymbol.fontStyle === 'bold'
                    || scaleBar.textSymbol.fontStyle === 'boldItalic'
                      ? tokens.font.weight.bold
                      : tokens.font.weight.regular,
                  fontStyle:
                    scaleBar.textSymbol.fontStyle === 'italic'
                    || scaleBar.textSymbol.fontStyle === 'boldItalic'
                      ? 'italic'
                      : 'normal',
                }}
              >
                Aa
              </button>
            </div>
          </Section>

          <Section
            id="fitting"
            title="Fitting Strategy"
            open={openSections.has('fitting')}
            onToggle={toggleSection}
          >
            <select
              aria-label="Scale bar fitting strategy"
              value={scaleBar.fittingStrategy}
              onChange={(event) => {
                const fittingStrategy =
                  event.target.value as ScaleBarFittingStrategy;
                const preserveDivision =
                  fittingStrategy === 'adjustDivisions'
                  || fittingStrategy === 'adjustWidth';
                onChange({
                  fittingStrategy,
                  ...(preserveDivision
                    ? {
                        upper: {
                          divisionValue: geometry?.upper.divisionValue ?? null,
                        },
                        lower: scaleBar.lower
                          ? {
                              divisionValue:
                                geometry?.lower?.divisionValue ?? null,
                            }
                          : undefined,
                      }
                    : {}),
                  ...(fittingStrategy === 'fixedBarWidth'
                    ? { fixedBarWidthMm: geometry?.upper.widthMm ?? scaleBar.fixedBarWidthMm }
                    : {}),
                });
              }}
              style={field}
            >
              {FITTING.filter((option) => !dual || option.dual).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {scaleBar.fittingStrategy === 'fixedBarWidth' ? (
              <label style={row}>
                <span>Bar width</span>
                <input
                  aria-label="Fixed scale bar width"
                  type="number"
                  min={1}
                  max={1000}
                  step={0.5}
                  value={scaleBar.fixedBarWidthMm}
                  onChange={(event) =>
                    onChange({
                      fixedBarWidthMm: boundedNumber(
                        event.target.value,
                        1,
                        1000,
                        scaleBar.fixedBarWidthMm,
                      ),
                    })
                  }
                  style={field}
                />
              </label>
            ) : null}
          </Section>

          <Section
            id="divisions"
            title="Divisions"
            open={openSections.has('divisions')}
            onToggle={toggleSection}
          >
            {divisionEditor(
              dual ? 'Upper' : 'Scale bar',
              scaleBar.upper,
              'upper',
              geometry?.upper.divisionValue,
            )}
            {dual && scaleBar.lower
              ? divisionEditor(
                  'Lower',
                  scaleBar.lower,
                  'lower',
                  geometry?.lower?.divisionValue,
                )
              : null}
          </Section>
        </div>
      )}
    </aside>
  );
}
